import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import { EmptyState, ErrorNotice, Loading } from '../components/Feedback';
import { SenoIcon } from '../components/SenoIcon';
import { get, post } from '../lib/api';
import type { ContentBlock, Section } from '../types';

type RevisionTask = {
  id: string;
  instruction: string;
  target_block_id: string | null;
  proposed_content_json: string | null;
  status: string;
};

export function RevisionPage({ projectId }: { projectId: string }) {
  const [tasks, setTasks] = useState<RevisionTask[]>([]);
  const [sections, setSections] = useState<Section[]>([]);
  const [blocks, setBlocks] = useState<ContentBlock[]>([]);
  const [drafts, setDrafts] = useState<Record<string, { blockId: string; text: string }>>({});
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const load = useCallback(async () => {
    try {
      const [revision, document] = await Promise.all([
        get<{ tasks: RevisionTask[] }>(`/api/projects/${projectId}/revisions`),
        get<{ sections: Section[]; blocks: ContentBlock[] }>(`/api/projects/${projectId}/document`)
      ]);
      setTasks(revision.tasks);
      setSections(document.sections);
      setBlocks(document.blocks);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Ruang revisi tidak dapat dimuat.');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => void load(), [load]);

  const blockLabels = useMemo(
    () =>
      blocks.map((block) => ({
        ...block,
        label: `${sections.find((section) => section.id === block.sectionId)?.title ?? 'Bagian'} · Blok ${block.position}`
      })),
    [blocks, sections]
  );

  async function createSession(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const formElement = event.currentTarget;
    const form = new FormData(formElement);
    const file = form.get('revisionFile');
    const notes = String(form.get('notes') ?? '').trim();
    setWorking('create');
    setError('');
    setNotice('');
    let uploadedThisAttempt = false;
    try {
      let attachmentId: string | null = null;
      let inputType: 'text' | 'docx' | 'pdf' | 'image' = 'text';
      if (file instanceof File && file.size > 0) {
        const lowerName = file.name.toLocaleLowerCase('id-ID');
        if (file.type === 'application/pdf' || lowerName.endsWith('.pdf')) inputType = 'pdf';
        else if (
          file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
          lowerName.endsWith('.docx')
        )
          inputType = 'docx';
        else if (
          file.type.startsWith('image/') ||
          /\.(jpe?g|png|webp)$/.test(lowerName)
        )
          inputType = 'image';
        else throw new Error('Berkas revisi harus DOCX, PDF, JPG, PNG, atau WebP.');
        const upload = new FormData();
        upload.set('file', file);
        upload.set('category', 'revision');
        upload.set('aiReadable', 'true');
        const result = await post<{ file: { id: string } }>(
          `/api/projects/${projectId}/attachments`,
          upload
        );
        attachmentId = result.file.id;
        uploadedThisAttempt = true;
      }
      if (!attachmentId && notes.length < 3)
        throw new Error('Tempel catatan dosen atau pilih berkas revisi.');
      const result = await post<{ tasks?: Array<{ instruction: string }>; session?: { parseMethod?: string } }>(
        `/api/projects/${projectId}/revisions`,
        {
          inputType,
          sourceText: notes,
          attachmentId
        }
      );
      formElement.reset();
      await load();
      if (result.tasks?.length) {
        const method =
          result.session?.parseMethod === 'docx-comments'
            ? 'Komentar DOCX dibaca langsung.'
            : result.session?.parseMethod === 'ai-file'
              ? 'Berkas dibaca Seno AI dan hasilnya masih perlu Anda tinjau.'
              : 'Catatan teks berhasil diproses.';
        setNotice(`${result.tasks.length} tugas revisi siap ditinjau. ${method}`);
      } else {
        setError(
          'Berkas sudah tersimpan, tetapi belum menghasilkan tugas revisi yang dapat ditinjau. Jangan upload berulang; gunakan catatan teks sementara bila perlu.'
        );
      }
    } catch (caught) {
      const message =
        caught instanceof Error ? caught.message : 'Catatan revisi belum berhasil diproses.';
      setError(
        uploadedThisAttempt
          ? `${message} Berkas sudah tersimpan; jangan upload ulang berkali-kali.`
          : message
      );
    } finally {
      setWorking('');
    }
  }

  function selectBlock(taskId: string, blockId: string) {
    const block = blocks.find((item) => item.id === blockId);
    setDrafts((current) => ({
      ...current,
      [taskId]: { blockId, text: String(block?.content.text ?? '') }
    }));
  }

  async function propose(task: RevisionTask) {
    const draft = drafts[task.id];
    if (!draft?.blockId) return;
    setWorking(task.id);
    setError('');
    try {
      const result = await post<{
        candidate: {
          status: string;
          candidate: { text: string };
          validation: { issues: unknown[] };
        };
      }>(`/api/projects/${projectId}/ai-candidates`, {
        taskType: 'revision_apply_draft',
        targetType: 'block',
        targetId: draft.blockId,
        instruction: task.instruction,
        relevantObjectIds: [draft.blockId],
        idempotencyKey: `revision-${task.id}-${draft.blockId}-${crypto.randomUUID()}`
      });
      if (result.candidate.status !== 'valid') {
        setError('Usulan ditahan karena belum lolos pemeriksaan fakta atau sumber.');
      } else {
        setDrafts((current) => ({
          ...current,
          [task.id]: { ...draft, text: result.candidate.candidate.text }
        }));
      }
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Seno AI belum berhasil membuat usulan.');
    } finally {
      setWorking('');
    }
  }

  async function apply(task: RevisionTask) {
    const draft = drafts[task.id];
    const block = blocks.find((item) => item.id === draft?.blockId);
    if (!draft || !block) return;
    setWorking(task.id);
    setError('');
    try {
      await post(`/api/projects/${projectId}/revisions/${task.id}/apply`, {
        targetBlockId: draft.blockId,
        proposedText: draft.text,
        expectedRevision: block.revision
      });
      await load();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Revisi belum dapat diterapkan.');
    } finally {
      setWorking('');
    }
  }

  if (loading) return <Loading label="Membuka ruang revisi…" />;
  return (
    <div className="content-page revision-page">
      <div className="content-heading">
        <div>
          <span className="eyebrow">Tinjau sebelum menerapkan</span>
          <h1>Revisi dosen</h1>
          <p>
            Catatan dipecah menjadi tugas. Perubahan selalu tampil sebagai usulan dan mempunyai
            snapshot.
          </p>
        </div>
      </div>
      {error ? <ErrorNotice message={error} /> : null}
      {notice ? <p className="success-note">{notice}</p> : null}
      <form className="revision-input" onSubmit={createSession}>
        <div className="revision-source-grid">
          <label>
            Tempel catatan revisi — satu instruksi per baris
            <textarea
              name="notes"
              rows={6}
              placeholder="Tempel teks catatan dosen di sini, satu instruksi per baris."
            />
          </label>
          <label className="revision-file-field">
            Atau upload berkas dari dosen
            <input type="file" name="revisionFile" accept=".docx,.pdf,.jpg,.jpeg,.png,.webp" />
            <small>
              DOCX berkomentar dibaca langsung bila memungkinkan. PDF, screenshot, dan foto
              dianalisis Seno AI lalu hasilnya tetap harus Anda tinjau.
            </small>
          </label>
        </div>
        <button className="button button-primary" disabled={working === 'create'}>
          <SenoIcon name="revisi" /> {working === 'create' ? 'Memproses…' : 'Buat daftar revisi'}
        </button>
      </form>
      {tasks.length === 0 ? (
        <EmptyState
          title="Belum ada tugas revisi"
          description="Tempel catatan atau upload DOCX/PDF/screenshot dari dosen. Hasil pembacaan selalu ditinjau sebelum diterapkan."
        />
      ) : (
        <div className="revision-task-list">
          {tasks.map((task, index) => {
            const draft = drafts[task.id];
            const selected = blocks.find((block) => block.id === draft?.blockId);
            return (
              <article key={task.id} className={task.status === 'applied' ? 'applied' : ''}>
                <header>
                  <span>{index + 1}</span>
                  <div>
                    <strong>{task.instruction}</strong>
                    <small>
                      {task.status === 'applied' ? 'Sudah diterapkan' : 'Menunggu review'}
                    </small>
                  </div>
                </header>
                {task.status !== 'applied' ? (
                  <div className="revision-workspace">
                    <label>
                      Bagian yang terdampak
                      <select
                        value={draft?.blockId ?? ''}
                        onChange={(event) => selectBlock(task.id, event.target.value)}
                      >
                        <option value="">Pilih blok dokumen</option>
                        {blockLabels.map((block) => (
                          <option key={block.id} value={block.id}>
                            {block.label}
                          </option>
                        ))}
                      </select>
                    </label>
                    {selected ? (
                      <div className="diff-grid">
                        <label>
                          Versi sekarang
                          <textarea rows={8} value={String(selected.content.text ?? '')} readOnly />
                        </label>
                        <label>
                          Usulan
                          <textarea
                            rows={8}
                            value={draft.text}
                            onChange={(event) =>
                              setDrafts((current) => ({
                                ...current,
                                [task.id]: { ...draft, text: event.target.value }
                              }))
                            }
                          />
                        </label>
                      </div>
                    ) : null}
                    <div className="row-actions">
                      <button
                        className="button button-secondary"
                        disabled={!selected || working === task.id}
                        onClick={() => propose(task)}
                      >
                        <SenoIcon name="ai" /> Susun usulan
                      </button>
                      <button
                        className="button button-primary"
                        disabled={!selected || !draft?.text || working === task.id}
                        onClick={() => apply(task)}
                      >
                        Terapkan yang disetujui
                      </button>
                    </div>
                  </div>
                ) : null}
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}
